import { ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material";
import { MitigationStatusMenuGroup } from "./RiskMitigationOverview.hooks";

interface MitigationStatusMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    groups: Array<MitigationStatusMenuGroup>;
    onSelect: (id: string) => void;
    onClose: () => void;
    onExited: () => void;
}

// The status picker popover: ordered items, optional section headings (null header → no heading), disabled
// items carry a tooltip.
// `open` is decoupled from `anchorEl`/`groups` so the anchor and items stay mounted through the close
// transition — clearing them synchronously made MUI render an anchorless, empty menu (a small bubble in
// the viewport corner) for the duration of the fade-out. The parent clears them on `onExited` instead.
function MitigationStatusMenu({ anchorEl, open, groups, onSelect, onClose, onExited }: MitigationStatusMenuProps) {
    return (
        <Menu anchorEl={anchorEl} open={open} onClose={onClose} TransitionProps={{ onExited }}>
            {groups.flatMap((group, groupIndex) => [
                group.header ? (
                    <ListSubheader
                        key={`header-${groupIndex}`}
                        sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold, color: theme.palette.primary.dark, lineHeight: 2.2 })}
                    >
                        {group.header}
                    </ListSubheader>
                ) : null,
                ...group.items.map((option) => {
                    const menuItem = (
                        <MenuItem key={option.id} disabled={option.disabled} selected={option.isCurrent} onClick={() => onSelect(option.id)}>
                            {option.name}
                        </MenuItem>
                    );

                    return option.disabled && option.disabledHoverText ? (
                        <Tooltip key={option.id} title={option.disabledHoverText} placement={"left"}>
                            <span>{menuItem}</span>
                        </Tooltip>
                    ) : (
                        menuItem
                    );
                }),
            ])}
        </Menu>
    );
}

export default MitigationStatusMenu;
